const OFFICE_START_TIME = 9.5 // 9:30 AM
const OFFICE_END_TIME = 18 // 6:00 PM
const MINUTES_IN_HOUR = 60

const normalizeHolidayType = (hd) => {
  return (hd.type || hd.holiday_type || '')
    .toLowerCase()
    .replace(/[-\s]/g, '_')
}

export const buildHolidaySet = (holidayList = []) => {
  const set = new Set()
  holidayList.forEach((holiday) => {
    const { y, m, d } = holiday
    d.forEach((hd) => {
      const type = normalizeHolidayType(hd)

      if (type !== 'restricted_holiday') {
        const key = `${y}-${m}-${hd.day}`
        set.add(key)
      }
    })
  })
  return set
}

export const isHolidayFast = (date, holidaySet) => {
  const key = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
  return holidaySet.has(key)
}

export const getOfficeHoursInDay = (date, holidaySet) => {
  if (isHolidayFast(date, holidaySet)) return 0
  return OFFICE_END_TIME - OFFICE_START_TIME // 8.5 hours
}

export const calculateEffectiveMinutes = (
  startTime,
  endTime,
  holidayList = [],
) => {
  if (!startTime || !endTime) return 0
  const holidaySet = buildHolidaySet(holidayList)
  const startDay = new Date(startTime)
  const endDay = new Date(endTime)

  if (startDay > endDay) return 0
  const startDateOnly = new Date(startDay.toDateString())
  const endDateOnly = new Date(endDay.toDateString())

  // SAME DAY
  if (startDateOnly.getTime() === endDateOnly.getTime()) {
    if (isHolidayFast(startDay, holidaySet)) return 0

    const startHour = Math.max(
      OFFICE_START_TIME,
      startDay.getHours() + startDay.getMinutes() / MINUTES_IN_HOUR,
    )

    const endHour = Math.min(
      OFFICE_END_TIME,
      endDay.getHours() + endDay.getMinutes() / MINUTES_IN_HOUR,
    )

    return Math.max(0, (endHour - startHour) * MINUTES_IN_HOUR)
  }

  let totalMinutes = 0

  // FIRST DAY
  if (!isHolidayFast(startDay, holidaySet)) {
    const firstDayStartHour = Math.max(
      OFFICE_START_TIME,
      startDay.getHours() + startDay.getMinutes() / MINUTES_IN_HOUR,
    )

    totalMinutes += Math.max(
      0,
      (OFFICE_END_TIME - firstDayStartHour) * MINUTES_IN_HOUR,
    )
  }

  // MIDDLE DAYS
  const currentDay = new Date(startDateOnly)
  currentDay.setDate(currentDay.getDate() + 1)

  while (currentDay < endDateOnly) {
    if (!isHolidayFast(currentDay, holidaySet)) {
      totalMinutes +=
        (OFFICE_END_TIME - OFFICE_START_TIME) * MINUTES_IN_HOUR
    }
    currentDay.setDate(currentDay.getDate() + 1)
  }

  // LAST DAY
  if (!isHolidayFast(endDay, holidaySet)) {
    const lastDayEndHour = Math.min(
      OFFICE_END_TIME,
      endDay.getHours() + endDay.getMinutes() / MINUTES_IN_HOUR,
    )
    totalMinutes += Math.max(
      0,
      (lastDayEndHour - OFFICE_START_TIME) * MINUTES_IN_HOUR,
    )
  }

  return Math.max(0, totalMinutes)
}

export const minutesToHours = (minutes) => {
  return minutes / MINUTES_IN_HOUR
}

export const minutesToDays = (minutes) => {
  const workingHoursPerDay = OFFICE_END_TIME - OFFICE_START_TIME
  return minutes / (workingHoursPerDay * MINUTES_IN_HOUR)
}

export const OFFICE_CONFIG = {
  OFFICE_START_TIME,
  OFFICE_END_TIME,
  MINUTES_IN_HOUR,
}